import { Link } from 'react-router-dom';
import { Seo, Mark, Container } from '../components/marketing/Primitives';
import CareerArc from '../components/marketing/CareerArc';

/* The one deliberately bold element on the site: a specimen examination item,
   sealed and traced to its designated source. The artifact is the argument. */
function SpecimenItem() {
  return (
    <div className="hero-rise-3 relative mx-auto w-full max-w-md" aria-hidden="true">
      <div className="specimen-card">
        <div className="flex items-baseline justify-between">
          <p className="text-[11px] font-semibold tracking-wide text-slate">Specimen item</p>
          <p className="text-[11px] text-faint">Review: approved</p>
        </div>
        <p className="mt-3 text-[13px] leading-relaxed text-bright">
          A 54-year-old presents six weeks after rhytidectomy with a firm, discrete submandibular
          mass. The most appropriate next step is:
        </p>
        <ul className="mt-3 space-y-1.5 text-[13px] text-slate">
          <li className="flex gap-2"><span className="font-semibold text-bright">A.</span> Observation and reassurance</li>
          <li className="flex gap-2 rounded bg-seal/10 px-2 py-0.5 -mx-2">
            <span className="font-semibold text-bright">B.</span>
            <span className="text-bright">Ultrasound-guided evaluation</span>
            <span className="ml-auto text-[11px] font-semibold text-seal">key</span>
          </li>
          <li className="flex gap-2"><span className="font-semibold text-bright">C.</span> Empiric antibiotic therapy</li>
          <li className="flex gap-2"><span className="font-semibold text-bright">D.</span> Immediate operative exploration</li>
        </ul>
        <div className="mt-4 flex items-end justify-between border-t border-hairline pt-3">
          <p className="max-w-[24ch] text-[11px] leading-snug text-faint">
            Decided by an expert physician. Recorded, dated, defensible.
          </p>
          <div className="specimen-seal">
            <span className="font-serif text-sm leading-none">SK</span>
            <span className="mt-0.5 block text-[6px] tracking-[0.14em]">CERTIFIED</span>
          </div>
        </div>
      </div>
      {/* Provenance trace: the chain from item to designated source. */}
      <div className="ml-10 flex gap-3">
        <div className="trace-line" />
        <div className="specimen-source">
          <p className="text-[11px] font-semibold text-paper-dark-text">Designated source</p>
          <p className="mt-1 text-[11px] leading-snug text-slate-dark-text">
            Board reading list, item 14. Claim verified against source, 3 of 3.
          </p>
        </div>
      </div>
    </div>
  );
}

const argueLeft = [
  'An editor decides what makes the cut.',
  'An algorithm ranks what surfaces.',
  'A crawl aggregates what exists.',
];

const argueRight = [
  { lead: 'The board designates the sources', rest: 'its diplomates are accountable for.' },
  { lead: 'The blueprint scopes the knowledge', rest: 'a jurisdiction has determined is valid.' },
  { lead: 'The chain proves the origin', rest: 'of every claim, source to delivered word.' },
];

export default function HomePage() {
  return (
    <>
      <Seo
        description="EdAI Systems transforms the knowledge that medical licensing and certifying authorities define as valid into every modality of education and assessment: exam items, spaced repetition, simulated patients, clinical skills assessment, and continuous competency."
      />

      {/* Hero: the claim on the left, the artifact on the right. */}
      <section className="overflow-hidden bg-ink text-paper-dark-text">
        <Container className="grid items-center gap-14 py-20 md:py-28 lg:grid-cols-[1.15fr_1fr] lg:gap-10">
          <div>
            <h1 className="hero-rise font-serif text-display leading-[1.04] tracking-[-0.01em]">
              The Standard<br />of Knowledge
            </h1>
            <p className="hero-rise-2 mt-7 max-w-measure text-lg leading-relaxed text-paper-dark-text md:text-xl">
              Just as the standard of care defines what a physician must do, the Standard of
              Knowledge defines what a physician must know. EdAI turns it into everything needed
              to teach it, learn it, and assess it.
            </p>
            <div className="hero-rise-3 mt-10 flex flex-wrap gap-4">
              <Link to="/contact" className="btn-primary-on-dark">Request a demo</Link>
              <Link to="/who-its-for" className="btn-secondary-on-dark">Find your platform</Link>
            </div>
          </div>
          <SpecimenItem />
        </Container>
      </section>

      {/* The argument: two ways to derive knowledge. */}
      <section className="py-20 md:py-28">
        <Container>
          <h2 className="max-w-[26ch] font-serif text-3xl leading-tight md:text-4xl">
            Every reference platform decides for you what you should know. We never decide.
          </h2>
          <div className="mt-12 grid overflow-hidden rounded-media border border-hairline md:grid-cols-2">
            <div className="bg-surface p-8 md:p-10">
              <p className="font-serif text-xl text-slate">Editorially derived</p>
              <p className="mt-1 text-sm text-faint">Every aggregator, however good.</p>
              <ul className="mt-7 space-y-5">
                {argueLeft.map((line) => (
                  <li key={line} className="border-l-2 border-hairline pl-4 leading-relaxed text-slate">
                    {line}
                  </li>
                ))}
              </ul>
              <p className="mt-8 text-sm italic leading-relaxed text-faint">
                Comprehensive, current, and defined by the vendor's own judgment.
              </p>
            </div>
            <div className="bg-ink p-8 text-paper-dark-text shadow-[inset_0_0_80px_rgba(45,212,191,0.06)] md:p-10">
              <p className="font-serif text-xl">Authority-derived</p>
              <p className="mt-1 text-sm text-slate-dark-text">EdAI, and nothing else.</p>
              <ul className="mt-7 space-y-5">
                {argueRight.map((line) => (
                  <li key={line.lead} className="border-l-2 border-seal pl-4 leading-relaxed">
                    <span className="text-paper-dark-text">{line.lead}</span>{' '}
                    <span className="text-slate-dark-text">{line.rest}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-8 border-t border-hairline-dark pt-6 font-serif italic leading-relaxed text-seal-soft">
                Aggregators give you everything. We give you what you are accountable for.
              </p>
            </div>
          </div>
        </Container>
      </section>

      {/* The arc, with the platforms inside it. */}
      <section className="border-y border-hairline bg-surface py-20 md:py-28">
        <Container>
          <Mark>The whole arc</Mark>
          <h2 className="mt-3 max-w-[24ch] font-serif text-3xl leading-tight md:text-4xl">
            One validated corpus, across an entire medical career.
          </h2>
          <div className="mt-14">
            <CareerArc />
          </div>
        </Container>
      </section>

      {/* Proof: lead with the strongest number, then the record. */}
      <section className="bg-ink-deep py-20 text-paper-dark-text md:py-24">
        <Container>
          <div className="grid gap-12 lg:grid-cols-[1fr_1.1fr] lg:gap-20">
            <div>
              <p className="font-serif text-7xl md:text-8xl">68.65%</p>
              <p className="mt-4 max-w-measure leading-relaxed text-slate-dark-text">
                What the leading general-purpose medical AI scored on EdAI certification items,
                given open-book access to the source articles. Below the passing threshold.
              </p>
              <p className="mt-5 max-w-measure font-serif italic leading-relaxed text-seal-soft">
                Content access is not content mastery.
              </p>
            </div>
            <dl className="grid content-center gap-10 border-hairline-dark sm:grid-cols-3 lg:border-l lg:pl-16">
              <div>
                <dd className="font-serif text-4xl">87%</dd>
                <dt className="mt-2 text-sm leading-relaxed text-slate-dark-text">Less expert physician time per examination item</dt>
              </div>
              <div>
                <dd className="font-serif text-4xl">0</dd>
                <dt className="mt-2 text-sm leading-relaxed text-slate-dark-text">AI-attributed factual errors in production</dt>
              </div>
              <div>
                <dd className="font-serif text-4xl">100%</dd>
                <dt className="mt-2 text-sm leading-relaxed text-slate-dark-text">Client retention</dt>
              </div>
            </dl>
          </div>
        </Container>
      </section>

      {/* Founder */}
      <section className="py-20 md:py-24">
        <Container>
          <div className="grid gap-10 md:grid-cols-[1fr_1.6fr] md:gap-16">
            <div>
              <Mark>The founder</Mark>
              <h2 className="mt-3 font-serif text-3xl md:text-4xl">Built by the examiner and the examined.</h2>
            </div>
            <div>
              <p className="leading-relaxed text-slate">
                John C. Ferguson, MD, FACS holds five board certifications and has spent more than
                ten years creating and administering specialty board examinations. He has lived both
                sides of the problem EdAI solves: the diplomate accountable to the standard, and the
                examiner responsible for defining and defending it.
              </p>
              <blockquote className="mt-6 border-l-2 border-seal pl-5 font-serif text-lg italic leading-relaxed">
                The boards already know what physicians must know. What was missing was the
                infrastructure to use that knowledge at scale, without ever compromising what makes
                it valid.
              </blockquote>
              <p className="mt-6">
                <Link to="/about" className="link-quiet text-sm font-semibold">Read the full story</Link>
              </p>
            </div>
          </div>
        </Container>
      </section>

      {/* Closing */}
      <section className="border-t border-hairline bg-surface py-20">
        <Container className="text-center">
          <h2 className="mx-auto max-w-[28ch] font-serif text-3xl md:text-4xl">
            What medicine requires you to know. Every way you need to use it.
          </h2>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Link to="/contact" className="btn-primary">Request a demo</Link>
            <Link to="/who-its-for" className="btn-secondary">Find your platform</Link>
          </div>
        </Container>
      </section>
    </>
  );
}
